import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, ReplaySubject, forkJoin, map } from 'rxjs';
import { NameType } from '../../shared/models/models';
import { StandardNamn } from '../../shared/models/standard-namn';
import { ApiService } from 'src/app/api/api.service';
import { HttpHeaders } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class StandardNamnService {
  private apiService = inject(ApiService);
  private urls = {
    SMITTAMNE: 'namelists/{nameType}/standardName',
    ANTIBIOTIKUM: 'namelists/{nameType}/standardName',
    ANALYS: 'namelists/{nameType}/standardName',
    PROVMATERIAL: 'namelists/{nameType}/standardName',
    NEGATIVT: 'namelists/{nameType}/standardName',
    BESKRIVANDE: 'namelists/{nameType}/standardName',
    UPDATE: 'namelists/{nameType}/{id}/standardName'
  };

  private loaded = false;

  public smittamne = new BehaviorSubject<StandardNamn[]>([]);
  public analyser = new BehaviorSubject<StandardNamn[]>([]);
  public provmaterial = new BehaviorSubject<StandardNamn[]>([]);
  public antibiotika = new BehaviorSubject<StandardNamn[]>([]);
  public negativa = new BehaviorSubject<StandardNamn[]>([]);
  public beskrivande = new BehaviorSubject<StandardNamn[]>([]);

  public load(forceReload?: boolean) {
    if (this.loaded && !forceReload) {
      return;
    }

    forkJoin([
      this.getStandardNamnAsObservale(NameType.Smittamne),
      this.getStandardNamnAsObservale(NameType.Provmaterial),
      this.getStandardNamnAsObservale(NameType.Analys),
      this.getStandardNamnAsObservale(NameType.Antibiotika),
      this.getStandardNamnAsObservale(NameType.NegativtFynd),
      this.getStandardNamnAsObservale(NameType.BeskrivandeFynd),
    ]).subscribe((data) => {
      this.smittamne.next(this.sortByStandardNamn(data[0]));
      this.provmaterial.next(this.sortByStandardNamn(data[1]));
      this.analyser.next(this.sortByStandardNamn(data[2]));
      this.antibiotika.next(this.sortByStandardNamn(data[3]));
      this.negativa.next(this.sortByStandardNamn(data[4]));
      this.beskrivande.next(this.sortByStandardNamn(data[5]));
      this.loaded = true;
    });
  }

  private sortByStandardNamn(array: StandardNamn[]) {
    return array.sort((a,b) => a.standardNamn > b.standardNamn ? 1 : -1)
  }

  public getStandardNamn(type: NameType) {
    const url = this.urls[type].replace('{nameType}', type);
    return this.apiService.get<StandardNamn[]>(url).catch((error) => []);
  }

  private getStandardNamnAsObservale(type: NameType) {
    const url = this.urls[type].replace('{nameType}', type);
    return this.apiService.getAsObservable<StandardNamn[]>(url);
  }

  public getStandardNamnByType(type: NameType, activeOnly?: boolean) {
    switch (type) {
      case NameType.Smittamne:
        return activeOnly ? this.activeOnlyPipe(this.smittamne) : this.smittamne;
      case NameType.Analys:
        return activeOnly ? this.activeOnlyPipe(this.analyser) : this.analyser;
      case NameType.Provmaterial:
        return activeOnly ? this.activeOnlyPipe(this.provmaterial) : this.provmaterial;
      case NameType.Antibiotika:
        return activeOnly ? this.activeOnlyPipe(this.antibiotika) : this.antibiotika;
      case NameType.NegativtFynd:
        return activeOnly ? this.activeOnlyPipe(this.negativa) : this.negativa;
      case NameType.BeskrivandeFynd:
        return activeOnly ? this.activeOnlyPipe(this.beskrivande) : this.beskrivande;
      default:
        return;
    }
  }

  private activeOnlyPipe(list$: BehaviorSubject<StandardNamn[]>) {
    return list$.pipe(map(namn => namn.filter(x => x.status === 'AKTIV')))
  }

  public updateStandardnamn(nameType: NameType, id: string, newName: string) {
    const url = this.urls['UPDATE'].replace('{nameType}', nameType).replace('{id}', id);
    const headers = new HttpHeaders().set('Content-Type', 'text/plain');
    return this.apiService.putAsObservable(url, newName, headers )
  }
}
